import React, { useState } from "react";
import { CalendarRange, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@pgs-core/ui/components/card";
import { Badge } from "@pgs-core/ui/components/badge";
import { Button } from "@pgs-core/ui/components/button";
import { Input } from "@pgs-core/ui/components/input";
import { Label } from "@pgs-core/ui/components/label";
import type { BillingTerm } from "@pgs-core/types";
import type { ApiClient } from "../lib/api";
import { clearClientTerm, setClientTerm } from "../lib/billing";

interface BillingTermCardProps {
  api: ApiClient | null;
  clientId: string;
  term: BillingTerm | null;
  onChanged: (notice: string) => void;
  onError: (message: string) => void;
}

/**
 * Fixed-term editor: setting a term snapshots today's plan + module prices as
 * the locked fixed-phase rates; clearing it puts the client on rolling pricing.
 */
export function BillingTermCard({ api, clientId, term, onChanged, onError }: BillingTermCardProps) {
  const [working, setWorking] = useState(false);
  const [start, setStart] = useState("");
  const [months, setMonths] = useState("12");

  const run = async (notice: string, action: () => Promise<unknown>) => {
    if (!api || working) return;
    setWorking(true);
    try {
      await action();
      onChanged(notice);
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setWorking(false);
    }
  };

  const today = new Date().toISOString().slice(0, 10);
  const phase = term ? (today >= term.start && today < term.end ? "fixed" : today < term.start ? "upcoming" : "ended") : null;

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
        <div>
          <CardTitle className="text-base">Fixed term</CardTitle>
          <CardDescription>
            Locks today's prices for the term; afterwards the client rolls onto
            current standard pricing. Adjustments are unaffected by the switch.
          </CardDescription>
        </div>
        <CalendarRange className="h-4 w-4 shrink-0 text-muted-foreground" />
      </CardHeader>
      <CardContent className="space-y-3">
        {term ? (
          <>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <dt className="text-muted-foreground">Phase now</dt>
              <dd>
                <Badge variant={phase === "fixed" ? "success" : "secondary"}>
                  {phase === "fixed" ? "Fixed (locked prices)" : phase === "upcoming" ? "Starts soon" : "Rolling (term ended)"}
                </Badge>
              </dd>
              <dt className="text-muted-foreground">Term</dt>
              <dd>
                {term.start} → {term.end} ({term.months} months)
              </dd>
            </dl>
            <Button
              size="sm"
              variant="outline"
              disabled={working || !api}
              onClick={() => run("Fixed term removed — the client is on rolling pricing.", () => clearClientTerm(api!, clientId))}
            >
              {working ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Remove term
            </Button>
          </>
        ) : (
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="term-start">Start date</Label>
              <Input id="term-start" type="date" className="w-40" value={start} onChange={(e) => setStart(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="term-months">Length (months)</Label>
              <Input
                id="term-months"
                className="w-28"
                inputMode="numeric"
                value={months}
                onChange={(e) => setMonths(e.target.value)}
              />
            </div>
            <Button
              size="sm"
              disabled={working || !api || !start || !(parseInt(months, 10) >= 1)}
              onClick={() =>
                run("Fixed term set — current prices are locked for its duration.", () =>
                  setClientTerm(api!, clientId, start, parseInt(months, 10))
                )
              }
            >
              {working ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Set fixed term
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
